import { FormattedMessage, useIntl } from 'react-intl';
import { type MouseEvent } from 'react';
import { CommentBubbleIcon } from '@m/alchemy-ui/icons/CommentBubbleIcon';
import { Link } from '@m/alchemy-ui/Link';
import { type IBlog } from '../../../../common/interfaces';
import { AVATAR_PLACEHOLDER, NO_IMAGE_PLACEHOLDER } from '../../common/const';
import { decodeHtml } from '../../../../helpers';
import { MutedText } from '../../../community-page/components/Tabbed/common/styles';
import { formatUserName, isUserAccessRestricted } from '../../../../helpers/userAccess';
import {
  CardBody,
  CardBodyDescription,
  CardBodyTitle,
  CardFooter,
  CardFooterLink,
  CardHeader,
  CardHeaderImage,
  CardUser,
  CardUserAvatar,
  UserInfoContainer,
  UserName
} from './BlogItem.styled';
import { Card, StyledIcon } from './common/styles';
import { normalizeText } from './common/helpers';

declare function popUp(url: string, options: unknown): void;
interface IBlogItemProps {
  data: IBlog;
  itemsToDisplay: number;
  panelSize: number;
  isWide: boolean;
}

export const BlogItem = ({ data, itemsToDisplay, panelSize, isWide }: IBlogItemProps) => {
  const intl = useIntl();
  const openUserProfile = (e: MouseEvent<HTMLButtonElement>, userId: string) => {
    const url = `/user/view/${userId}`;
    // It is popUp in main js
    popUp(url, {
      popUpTitle: intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' }),
      height: 390,
      focusOnClose: e.currentTarget
    });
  };

  const decodedTitle = decodeHtml(data.title);
  const imageSrc = data.imageUrl || NO_IMAGE_PLACEHOLDER;
  const largeImageSrc = data.imageLargeUrl || NO_IMAGE_PLACEHOLDER;
  const normalizedTitle = panelSize > 1 && !isWide ? normalizeText(decodedTitle, 20) : decodedTitle;
  const description = panelSize > 1 ? normalizeText(data.description, 130) : data.description;
  const authorName = panelSize === 2 ? data.author.name.slice(0, 20) : data.author.name.slice(0, 35);
  const isAuthorAccessRestricted = isUserAccessRestricted(data.author?.userType);
  const displayName = formatUserName(authorName, isAuthorAccessRestricted, intl);

  return (
    <Card className="mdl-panel">
      <CardHeader>
        {itemsToDisplay > 1 || panelSize > 1 ? (
          <CardHeaderImage src={imageSrc} alt="" />
        ) : (
          <CardHeaderImage src={largeImageSrc} alt="" />
        )}
      </CardHeader>
      <CardBody>
        <CardUser>
          {isAuthorAccessRestricted ? (
            <CardUserAvatar src={data.author.avatarUrl || AVATAR_PLACEHOLDER} />
          ) : (
            <Link
              onClick={(e: MouseEvent<HTMLButtonElement>) => openUserProfile(e, data.author.id)}
              aria-hidden="true"
              tabIndex={-1}
            >
              <CardUserAvatar src={data.author.avatarUrl || AVATAR_PLACEHOLDER} />
            </Link>
          )}

          <UserInfoContainer>
            <FormattedMessage id="page.by" defaultMessage="by" />
            {isAuthorAccessRestricted ? (
              <MutedText>{displayName}</MutedText>
            ) : (
              <UserName
                aria-label={`${intl.formatMessage({ id: 'openProfileDialog', defaultMessage: 'Open profile dialog for' })} ${displayName}`}
                onClick={(e: MouseEvent<HTMLButtonElement>) => openUserProfile(e, data.author.id)}
              >
                {decodeHtml(displayName)}
              </UserName>
            )}
          </UserInfoContainer>
          <span data-testid="blog-item">{data.posted}</span>
        </CardUser>
        <CardBodyTitle>
          <a href={data.linkUrl} title={decodedTitle}>
            {normalizedTitle}
          </a>
        </CardBodyTitle>
        <CardBodyDescription>{description}</CardBodyDescription>
      </CardBody>
      <CardFooter>
        <CardFooterLink href={data.linkUrl}>
          <FormattedMessage id="page.readMore" defaultMessage="Read more" />
        </CardFooterLink>
        <StyledIcon>
          <CommentBubbleIcon aria-hidden="true" />
        </StyledIcon>
        {` ${data.commentCount || 0}`}
      </CardFooter>
    </Card>
  );
};
